import apiFindIp from '@/services/apiFindIp';
import { ipExtractionRegex } from '@/utils/regex';
import type {
  DiagnosticParam,
  TracerouteHopResult,
} from '@/services/diagnosticsService';
import type { HopType } from './types';

export { findParam } from '@/services/diagnosticsService';
export { getLatencyThresholdColor } from '@/utils/statusColor';

/**
 * Parses the raw hops array returned from backend/TR-069
 */
export function parseHopResults(
  rawHops: TracerouteHopResult[],
  fallbackHost: string
): HopType[] {
  if (!Array.isArray(rawHops) || rawHops.length === 0) {
    return [];
  }

  return rawHops.map((raw, index) => {
    const hopNumber = Number(
      raw.hop ?? raw.hopNumber ?? raw.index ?? index + 1
    );

    const rawHostAddress = String(
      raw.hostAddress ?? raw.hopHostAddress ?? raw.address ?? raw.ip ?? ''
    ).trim();

    const rawHost = String(
      raw.host ?? raw.hopHost ?? raw.name ?? raw.hostname ?? ''
    ).trim();

    let address = '';
    let name: string | null = null;

    // clean IP from rawHostAddress first
    const matchAddress = rawHostAddress.match(ipExtractionRegex);
    if (matchAddress) {
      address = matchAddress[1].trim();
    } else if (rawHostAddress) {
      address = rawHostAddress;
    }

    // extract IP from rawHost if address is still empty
    if (!address && rawHost) {
      const matchHostIp = rawHost.match(ipExtractionRegex);
      if (matchHostIp) {
        address = matchHostIp[1].trim();
      } else {
        address = rawHost;
      }
    }

    // Determine name/hostname
    if (rawHost) {
      const cleanHost = rawHost
        .replace(/\s*[([](\d{1,3}\.){3}\d{1,3}[)\]]/g, '')
        .trim();

      if (cleanHost && cleanHost !== address) {
        name = cleanHost;
      }
    }

    if (!name && raw.name && raw.name !== address) {
      name = String(raw.name).trim();
    }

    const rawLatency =
      raw.latency ??
      raw.hopRTTimes ??
      raw.rttTimes ??
      raw.rtt ??
      raw.responseTime ??
      '';

    let latency: string[] = [];
    if (Array.isArray(rawLatency)) {
      latency = rawLatency.map((item) => String(item).trim());
    } else if (typeof rawLatency === 'string' && rawLatency.trim() !== '') {
      if (rawLatency.includes(',')) {
        latency = rawLatency.split(',').map((s) => s.trim());
      } else if (rawLatency.includes('/')) {
        latency = rawLatency.split('/').map((s) => s.trim());
      } else {
        latency = [rawLatency.trim()];
      }
    } else if (typeof rawLatency === 'number') {
      latency = [`${rawLatency} ms`];
    } else {
      latency = ['*'];
    }

    latency = latency.map((lat) => {
      if (!lat || lat === '*') return '*';
      if (lat.endsWith('ms') || lat.endsWith('s')) return lat;
      const num = parseFloat(lat);
      return !isNaN(num) ? `${lat} ms` : lat;
    });

    if (latency.length === 0) {
      latency = ['*'];
    }

    const isLastHop = index === rawHops.length - 1;

    return {
      hop: hopNumber,
      name:
        name ||
        (isLastHop && fallbackHost && fallbackHost !== address
          ? fallbackHost
          : null),
      address: address || (isLastHop && fallbackHost ? fallbackHost : '*'),
      latency,
      lat: raw.lat ?? raw.latitude ?? undefined,
      lon: raw.lon ?? raw.longitude ?? undefined,
      city: raw.city ?? undefined,
      isp: raw.isp ?? undefined,
      subdivIso: raw.subdivIso ?? raw.subdivision ?? undefined,
    };
  });
}

/**
 * Enriches hops with geolocation using apiFindIp for hops
 */
export const fetchHopLocations = async (
  hops: HopType[],
  lang: string
): Promise<HopType[]> => {
  return Promise.all(
    hops.map(async (hop) => {
      if (hop.lat !== undefined && hop.lon !== undefined) {
        return hop;
      }
      const queryTarget =
        hop.address && hop.address !== '*'
          ? hop.address
          : hop.name && hop.name !== '*'
            ? hop.name
            : null;

      if (queryTarget) {
        try {
          const response = await apiFindIp(`${queryTarget}`);
          const data = response?.data;
          if (
            data != null &&
            data.location &&
            data.location.latitude !== undefined &&
            data.location.longitude !== undefined
          ) {
            return {
              ...hop,
              lat: data.location.latitude,
              lon: data.location.longitude,
              city:
                lang === 'pt'
                  ? data.city?.names?.['pt-BR'] ||
                    data.city?.names?.['pt'] ||
                    data.city?.names?.['en']
                  : data.city?.names?.[lang] || data.city?.names?.['en'],
              isp:
                data.traits?.isp ||
                data.traits?.organization ||
                data.traits?.autonomous_system_organization,
              subdivIso: data.subdivisions?.[0]?.iso_code,
            };
          }
        } catch (error) {
          console.error('Error fetching location for hop:', queryTarget, error);
        }
      }
      return hop;
    })
  );
};

export const TRACEROUTE_INPUT_SUFFIXES = [
  'Host',
  'DSCP',
  'NumberOfTries',
  'Timeout',
  'DataBlockSize',
  'MaxHopCount',
];

export function isTracerouteInput(param: DiagnosticParam): boolean {
  return TRACEROUTE_INPUT_SUFFIXES.some((suffix) =>
    param.name?.endsWith(suffix)
  );
}
